import React from 'react';
import clsx from 'clsx';

import SideDrawer from '../_custom/SideDrawer/SideDrawer';
import DataTable from '../_custom/DataTable/DataTable';
import PropertySearchInput from './PropertySearchInput';
import { useRowIdentityKey } from './useRowIdentityKey';
import { useViewAllPropertiesLogic } from './useViewAllPropertiesLogic';
import { DEFAULT_NOT_FOUND_MESSAGE, DRAWER_HEADING, DRAWER_WIDTH, NO_MATCHING_PROPERTIES_MESSAGE } from './constants';
import { ViewAllPropertiesDrawerProps } from './types';

import styles from './ViewAllPropertiesDrawer.module.scss';

const ViewAllPropertiesDrawer: React.FC<ViewAllPropertiesDrawerProps> = ({
    row,
    columns,
    heading,
    customCellRenderers,
    valueGetterMap,
    modalActionConfig,
    onHistoryClick,
    onClose,
    loading = false,
    error = false,
    notFound = false,
    notFoundMessage = DEFAULT_NOT_FOUND_MESSAGE,
}) => {
    const rowKey = useRowIdentityKey(row);
    const { searchQuery, setSearchQuery, visibleColumns, hasNoMatches, mergedCellRenderers } = useViewAllPropertiesLogic({
        columns,
        customCellRenderers,
        modalActionConfig,
        onClose,
        rowKey,
    });

    const renderBody = () => {
        if (loading) return <div className={styles.StateMessage}>Loading…</div>;
        if (error) return <div className={styles.StateMessage}>Couldn't load properties. Try again.</div>;
        if (notFound || !row) return <div className={styles.StateMessage}>{notFoundMessage}</div>;

        return (
            <div className={styles.Card}>
                {/* Always mounted so assistive tech announces the message when it appears. */}
                <div role="status" className={clsx({ [styles.StateMessage]: hasNoMatches })}>
                    {hasNoMatches ? NO_MATCHING_PROPERTIES_MESSAGE : null}
                </div>
                {!hasNoMatches && (
                    <div className={styles.NoHoverTable}>
                        <DataTable
                            horizontal
                            columns={visibleColumns}
                            rows={[row]}
                            totalRowCount={1}
                            emptyState={null}
                            customCellRenderers={mergedCellRenderers}
                            valueGetterMap={valueGetterMap}
                            onHistoryClick={onHistoryClick}
                            horizontalLabelSuffix=""
                            horizontalHistoryInLabel
                            horizontalLabelIds
                            horizontalLabelClassName={styles.PropertyLabel}
                            horizontalValueClassName={styles.PropertyValue}
                            // Row-mode column widths sum to a huge table; pin it to the drawer width.
                            horizontalStyle={{ width: '100%' }}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <SideDrawer
            width={DRAWER_WIDTH}
            onClose={onClose}
            showHeading
            ariaLabel={heading ? `${DRAWER_HEADING} - ${heading}` : DRAWER_HEADING}
            heading={<div className={styles.Heading}>{DRAWER_HEADING}</div>}
            headerActions={<PropertySearchInput value={searchQuery} onChange={setSearchQuery} />}
        >
            {/* Keyed so a different row mounts fresh editors, without replaying the drawer's slide-in. */}
            <div className={styles.Body} key={rowKey}>
                {renderBody()}
            </div>
        </SideDrawer>
    );
};

export default ViewAllPropertiesDrawer;
